import { useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getAllCategories, createCategory, updateCategory } from '../../lib/api'
import { categorySchema } from '../../lib/schemas'
import { slugify } from '../../lib/utils'
import { Input } from '../../components/ui/Input'
import { IconInput } from '../../components/ui/IconInput'
import { Toggle } from '../../components/ui/Toggle'
import { Button } from '../../components/ui/Button'
import { Spinner } from '../../components/ui/Spinner'

export default function CategoryForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: categories } = useQuery({ queryKey: ['categories'], queryFn: getAllCategories, enabled: isEdit })

  const { register, handleSubmit, control, reset, watch, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(categorySchema),
    defaultValues: { slug: '', label: '', icon: '', color: '', order: 0, isActive: true },
  })

  useEffect(() => {
    if (isEdit && categories) {
      const item = categories.find(c => c.id === id)
      if (item) reset(item)
    }
  }, [categories, id, isEdit, reset])

  const labelValue = watch('label')
  const iconValue = watch('icon')
  const colorValue = watch('color')

  useEffect(() => {
    if (!isEdit && labelValue) setValue('slug', slugify(labelValue))
  }, [labelValue, isEdit, setValue])

  const mutation = useMutation({
    mutationFn: (data) => isEdit ? updateCategory(id, data) : createCategory(data),
    onSuccess: (result) => {
      qc.setQueryData(['categories'], old =>
        isEdit
          ? (old ?? []).map(c => c.id === id ? result : c)
          : [...(old ?? []), result]
      )
      toast.success(isEdit ? 'Category updated' : 'Category created')
      navigate('/categories')
    },
    onError: (e) => toast.error(e.message),
  })

  if (isEdit && !categories) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>

  return (
    <div className="">
      <div className="flex items-center gap-3 mb-8">
        <Link to="/categories"><Button variant="ghost" size="sm"><i className="ti ti-arrow-left" /></Button></Link>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit Category' : 'New Category'}</h1>
          <p className="text-sm text-gray-500 mt-0.5">Business classification category</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-6">
        <section className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Details</h2>
          <Input label="Label" placeholder="Restaurant" error={errors.label?.message} {...register('label')} />
          <Input label="Slug" placeholder="restaurant" hint="Auto-generated from label. You can edit it manually." error={errors.slug?.message} {...register('slug')} />
          <div className="grid grid-cols-2 gap-4">
            <IconInput label="Icon class" error={errors.icon?.message} value={iconValue} {...register('icon')} />
            <Input label="Color (hex)" placeholder="#f97316" error={errors.color?.message} {...register('color')} />
          </div>
          <Input label="Sort order" type="number" placeholder="0" error={errors.order?.message} {...register('order')} />
        </section>

        {(watch('label') || iconValue || colorValue) && (
          <section>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Preview</h2>
            <div className="flex items-center gap-4 p-4 rounded-xl border border-dashed border-gray-200 bg-gray-50/50">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" style={{ background: colorValue || '#e5e7eb' }}>
                {iconValue && <i className={`ti ${iconValue} text-white text-xl`} />}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{watch('label') || 'Category name'}</p>
                <p className="text-xs text-gray-400 mt-0.5">{watch('slug') || 'category-slug'}</p>
              </div>
            </div>
          </section>
        )}

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Settings</h2>
          <Controller name="isActive" control={control} render={({ field }) => (
            <Toggle label="Active" description="Visible on the public site" checked={field.value} onChange={field.onChange} />
          )} />
        </section>

        <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
          <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
            {isEdit ? 'Update Category' : 'Create Category'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/categories')}>Cancel</Button>
        </div>
      </form>
    </div>
  )
}
